import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Truck, Dna, TestTube, Stamp } from "lucide-react";
import { startCheckout, getPricing } from "../lib/payments";
import { useTick } from "../hooks/useTick";
import { toast } from "sonner";

const ICONS = {
  "Medical Courier": Truck,
  "DNA Testing": Dna,
  "Drug Testing": TestTube,
  "Notary Services": Stamp,
};

export default function Pay() {
  const nav = useNavigate();
  const tick = useTick();
  const [pricing, setPricing] = useState(null);
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState("");

  useEffect(() => {
    getPricing().then(setPricing).catch(() => toast.error("Could not load pricing"));
  }, []);

  const pay = async (payload, key) => {
    tick(520);
    setLoading(key);
    try {
      await startCheckout({ ...payload, customer_email: email || undefined });
    } catch (e) {
      toast.error("Unable to start checkout. Please try again.");
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="grain-overlay" />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-8">
        <button
          data-testid="pay-back-home"
          onClick={() => nav("/")}
          className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft size={14} /> Back to site
        </button>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pb-28">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard size={18} className="text-primary" />
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">/ Secure Checkout</span>
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase leading-none mb-10">
          Make a <span className="text-primary">Payment</span>
        </h1>

        <label className="block max-w-md mb-14">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Your Email (for receipt)</span>
          <input
            data-testid="pay-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full bg-transparent border-b border-border focus:border-primary py-3 mt-1 font-mono text-sm outline-none transition-colors"
          />
        </label>

        {/* Fixed service prices */}
        <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-6">Service Prices</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {pricing &&
            Object.entries(pricing.services).map(([name, price]) => {
              const Icon = ICONS[name] || CreditCard;
              const key = `svc-${name}`;
              return (
                <div key={name} data-testid={`pay-service-${name.replace(/\s+/g, "-").toLowerCase()}`} className="border border-border bg-card p-7 flex flex-col">
                  <Icon size={24} className="text-primary mb-6" strokeWidth={1.4} />
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">{name}</div>
                  <div className="font-display font-black text-3xl tracking-tighter tabular-nums mt-2 mb-6">${price.toFixed(2)}</div>
                  <button
                    onClick={() => pay({ payment_type: "service", service: name }, key)}
                    disabled={!!loading}
                    className="mt-auto bg-primary text-primary-foreground font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-3 border border-primary hover:bg-transparent hover:text-primary transition-colors duration-300 disabled:opacity-50"
                  >
                    {loading === key ? "Redirecting..." : "Pay Now"}
                  </button>
                </div>
              );
            })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Deposit */}
          <div className="border border-border bg-card p-8">
            <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-3">Booking Deposit</h2>
            <p className="text-sm font-mono text-muted-foreground mb-6">A flat deposit that applies to any service and secures your slot.</p>
            <div className="font-display font-black text-4xl tracking-tighter tabular-nums mb-6">${pricing?.deposit?.toFixed(2) ?? "—"}</div>
            <button
              data-testid="pay-deposit"
              onClick={() => pay({ payment_type: "deposit" }, "deposit")}
              disabled={!!loading}
              className="bg-primary text-primary-foreground font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-primary hover:bg-transparent hover:text-primary transition-colors duration-300 disabled:opacity-50"
            >
              {loading === "deposit" ? "Redirecting..." : "Pay Deposit"}
            </button>
          </div>

          {/* Custom */}
          <div className="border border-border bg-card p-8">
            <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-3">Custom Amount</h2>
            <p className="text-sm font-mono text-muted-foreground mb-6">Pay an invoice or agreed amount.</p>
            <div className="flex items-center border-b border-border focus-within:border-primary mb-6">
              <span className="font-display font-black text-2xl pr-2">$</span>
              <input
                data-testid="pay-custom-amount"
                type="number"
                min="1"
                step="0.01"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="0.00"
                className="w-full bg-transparent py-3 font-display font-black text-2xl tabular-nums outline-none"
              />
            </div>
            <button
              data-testid="pay-custom-submit"
              onClick={() => {
                const amt = parseFloat(custom);
                if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
                pay({ payment_type: "custom", custom_amount: amt }, "custom");
              }}
              disabled={!!loading}
              className="bg-foreground text-background font-mono uppercase tracking-[0.12em] text-[11px] font-bold py-4 px-8 border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300 disabled:opacity-50"
            >
              {loading === "custom" ? "Redirecting..." : "Pay Custom Amount"}
            </button>
          </div>
        </div>

        <p className="text-xs font-mono text-muted-foreground mt-10">Payments processed securely by Stripe. Test mode — use card 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
